import React, { type ReactNode } from 'react';
import type { TabProductMap } from 'reactApp/sections/QuotaLanding/MultiTariffsList/hooks/useUpsaleProductsMapping';
import type {
    MultiTariffUpsaleArgs,
    MultiTariffUpsaleModelType,
    MultiTariffUpsaleRadioType,
} from 'reactApp/sections/QuotaLanding/multiTariffUpsaleDialog/models/MultiTariffUpsaleDialog.model.types';
import styles from 'reactApp/sections/QuotaLanding/multiTariffUpsaleDialog/MultiTariffUpsaleDialog.css';
import { economy } from 'reactApp/sections/QuotaLanding/multiTariffUpsaleDialog/useYearSavingWithDiscountPeriod';
import type { Product } from 'reactApp/types/Billing';
import { getPrice } from 'reactApp/ui/TariffSectionNew/helpers/getPrice';
import type { ETabsName } from 'reactApp/ui/TariffSectionNew/TariffSection.constants';
import { getFirstSomePeriodsAsWord, getPeriodName, isMonthPeriod, isYearPeriod } from 'reactApp/utils/Period';
import { formatPrice } from 'reactApp/utils/priceHelpers';
import { capitalizeFirstLetter } from 'reactApp/utils/string';

export abstract class MultiTariffUpsaleModelBase implements MultiTariffUpsaleModelType {
    protected product: Product;
    protected tabProductMap: TabProductMap;
    protected activeTab: ETabsName;

    constructor({ activeTab, product, tabProductMap }: MultiTariffUpsaleArgs) {
        this.product = product;
        this.tabProductMap = tabProductMap;
        this.activeTab = activeTab;
    }

    protected getProductPriceString(product?: Product) {
        if (!product) {
            return '';
        }
        const [_, __, ___, productMonthPriceAsText, defaultMonthPrice] = getPrice({ product, periodDivider: 'в' });
        const showCrossedPrice = !product.hasTrial && product.discountPrice && !this.isYearActivePeriod();
        return showCrossedPrice ? (
            <>
                <span className={styles.radioDecoration}>{defaultMonthPrice}</span> {productMonthPriceAsText}
            </>
        ) : (
            productMonthPriceAsText
        );
    }

    protected getProductDescription(product?: Product) {
        if (!product) {
            return '';
        }
        const descriptions: string[] = [];
        if (this.isYearActivePeriod()) {
            descriptions.push(`При покупке на\u00A0${getPeriodName(product.period)}`);
        }
        if (product.hasTrial) {
            descriptions.push(`${capitalizeFirstLetter(getFirstSomePeriodsAsWord(1, 'm'))} за\u00A00\u00A0₽`);
        } else if (product.discountPrice && !this.isYearActivePeriod()) {
            descriptions.push(
                `${capitalizeFirstLetter(
                    getFirstSomePeriodsAsWord(product.discountPeriodCount || 1, this.isMonthActivePeriod() ? 'm' : 'y')
                )}`
            );
        }

        return descriptions.join('. \n');
    }

    protected isMonthActivePeriod() {
        return isMonthPeriod(this.product.period);
    }

    protected isYearActivePeriod() {
        return isYearPeriod(this.product.period);
    }

    protected getMonthToYearEconomyString(upsaleProduct: Product) {
        return `Экономия ${formatPrice(economy(this.product, upsaleProduct), 'ru')} ₽`;
    }

    getSecondProduct() {
        return this.product;
    }

    getUpsaleProduct() {
        const period = this.isMonthActivePeriod() ? 'month' : 'year';
        return this.tabProductMap[this.activeTab]?.[period]?.[this.product.id];
    }

    getTitle(): ReactNode {
        return '';
    }

    getTitleDescription(): ReactNode {
        return '';
    }

    getPriorityCardTopTitle(): ReactNode {
        const targetProduct = this.getUpsaleProduct() || this.product;
        return `+${targetProduct.space.value}`;
    }

    getPriorityCardTitle(): ReactNode {
        const upsaleProduct = this.getUpsaleProduct();
        return this.getProductPriceString(upsaleProduct);
    }

    getPriorityCardDescription(): ReactNode {
        const upsaleProduct = this.getUpsaleProduct();
        return this.getProductDescription(upsaleProduct);
    }

    getPriorityCardLabelText(): ReactNode {
        return '';
    }

    getSecondaryCardTopTitle(): ReactNode {
        return `+${this.product.space.value}`;
    }

    getSecondaryCardTitle(): ReactNode {
        return this.getProductPriceString(this.product);
    }

    getSecondaryCardDescription(): ReactNode {
        return this.getProductDescription(this.product);
    }

    getSecondaryCardLabelText(): ReactNode {
        return '';
    }

    getSecondaryCardLabelVisible(): boolean {
        return true;
    }

    getDwh(_: MultiTariffUpsaleRadioType) {
        return {
            action: 'modal-choose-tariff',
        };
    }
}
